import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { faqAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminFAQ = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    is_active: true
  });
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    fetchFaq();
  }, []);

  const fetchFaq = async () => {
    try {
      const response = await faqAPI.getAllAdmin();
      setFaqItems(response.data);
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      toast.error('Ошибка загрузки FAQ');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      order: 0,
      is_active: true
    });
    setEditingFaq(null);
  };

  const openDialog = (faq = null) => {
    if (faq) {
      setFormData({
        question: faq.question,
        answer: faq.answer,
        order: faq.order,
        is_active: faq.is_active
      });
      setEditingFaq(faq);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      order: parseInt(formData.order)
    };

    try {
      if (editingFaq) {
        await faqAPI.update(editingFaq.id, submitData);
        toast.success('FAQ успешно обновлён');
      } else {
        await faqAPI.create(submitData);
        toast.success('FAQ успешно создан');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchFaq();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Ошибка сохранения FAQ');
    }
  };

  const handleDelete = async (faqId) => {
    if (confirm('Вы уверены, что хотите удалить этот вопрос?')) {
      try {
        await faqAPI.delete(faqId);
        toast.success('FAQ успешно удалён');
        fetchFaq();
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        toast.error('Ошибка удаления FAQ');
      }
    }
  };

  const toggleExpanded = (faqId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedItems(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление FAQ</h1>
          <p className="text-gray-400">Создание и редактирование часто задаваемых вопросов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-faq-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить вопрос
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingFaq ? 'Редактировать вопрос' : 'Создать новый вопрос'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Заполните вопрос и ответ для FAQ
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="question" className="text-gray-300">Вопрос</Label>
                <Input
                  id="question"
                  value={formData.question}
                  onChange={(e) => handleInputChange('question', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Как начать пользоваться ботом?"
                  required
                  data-testid="faq-question-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="answer" className="text-gray-300">Ответ</Label>
                <Textarea
                  id="answer"
                  value={formData.answer}
                  onChange={(e) => handleInputChange('answer', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
                  placeholder="Для начала работы с ботом отправьте команду /start..."
                  required
                  data-testid="faq-answer-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order" className="text-gray-300">Порядок отображения</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => handleInputChange('order', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="0"
                    data-testid="faq-order-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mt-8">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                      data-testid="faq-active-switch"
                    />
                    <Label htmlFor="is_active" className="text-gray-300">Активный</Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-faq-btn"
                >
                  {editingFaq ? 'Обновить' : 'Создать'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* FAQ List */}
      <div className="space-y-4">
        {faqItems.map((faq, index) => (
          <motion.div
            key={faq.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader 
                className="cursor-pointer"
                onClick={() => toggleExpanded(faq.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      {expandedItems.has(faq.id) ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-white text-lg">{faq.question}</CardTitle>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={faq.is_active ? 'bg-green-600' : 'bg-gray-600'}>
                      {faq.is_active ? 'Активен' : 'Неактивен'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialog(faq);
                      }}
                      className="text-blue-400 hover:text-blue-300"
                      data-testid={`edit-faq-${faq.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(faq.id);
                      }}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-faq-${faq.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {expandedItems.has(faq.id) && (
                <CardContent>
                  <div className="text-gray-300 whitespace-pre-wrap">
                    {faq.answer}
                  </div>
                  <div className="mt-4 text-sm text-gray-500">
                    Порядок: {faq.order}
                  </div>
                </CardContent>
              )}
            </Card>
          </motion.div>
        ))}
      </div>

      {faqItems.length === 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="text-center py-12">
            <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">FAQ пока не добавлены</p>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Добавить первый вопрос
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminFAQ;